import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import axios from "axios";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import {
  OverviewColumn,
  OverviewPagination,
  OverviewTable,
  StatPanel,
  StatTile,
  formatUTCDateTime,
  money,
} from "../shared/AffiliateOverview";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "stretch",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: "2em",
  },
  mainHeading: {
    textAlign: "center",
    marginBottom: "1em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  card: {
    width: "100%",
    maxWidth: 1200,
    marginTop: "1em",
    padding: "2em",
    alignSelf: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1em",
  },
  input: {
    marginTop: "1em",
    width: "100%",
    maxWidth: 420,
  },
  actions: {
    padding: "0 1em 1em 1em",
    display: "flex",
    gap: "1em",
    justifyContent: "center",
  },
  tableContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  transactionContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  table: {
    width: "100%",
  },
  tableCell: {
    padding: "8px 16px",
    border: "1px solid #e0e0e0",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  sectionTitle: {
    marginTop: "2em",
    marginBottom: "1em",
    color: theme.palette.primary.main,
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  helperText: {
    marginTop: "0.75em",
    color: theme.palette.grey[600],
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "1em",
    marginBottom: "1.5em",
  },
  statTile: {
    padding: "1em 1.2em",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
  },
  statValue: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: "0.8rem",
    color: theme.palette.grey[600],
    marginTop: "0.2em",
  },
  statSub: {
    fontSize: "0.75rem",
    color: theme.palette.grey[500],
  },
  warnTile: {
    borderColor: theme.palette.error.main,
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: "1em",
    padding: "1em 0",
  },
}));

interface AwinUserDetails {
  user_id: number;
  total_clicks: number;
  sprive_plus_status: string;
  sprive_plus_enabled: boolean;
  dashboard_landed_at: string | null;
}

interface AwinTransaction {
  [key: string]: any;
}

interface AwinStats {
  purchases: {
    total: number;
    pending: number;
    approved: number;
    declined: number;
    deleted: number;
    unattributed: number;
    approval_rate_pct: number;
  };
  value: {
    total_sale_amount: number;
    total_commission: number;
    approved_sale_amount: number;
    approved_commission: number;
  };
  rewards: {
    total: number;
    pending: number;
    earned: number;
    transferred: number;
    cancelled: number;
    gross_amount: number;
    haircut_amount: number;
    user_amount: number;
    paid_to_users: number;
  };
  reach: {
    distinct_users: number;
    distinct_advertisers: number;
  };
}

interface AwinTopBrand {
  brand_name: string | null;
  advertiser_id: string;
  purchases: number;
  approved_count: number;
  sale_amount: number;
  commission: number;
}

interface AwinRecentPurchase {
  [key: string]: any;
}

interface AwinApiResponse {
  status: boolean;
  message: string;
  data: Array<{
    stats?: AwinStats;
    top_brands?: AwinTopBrand[];
    recent_purchases?: AwinRecentPurchase[];
    pagination?: OverviewPagination;
    userDetails?: AwinUserDetails[];
    transactionDetails?: AwinTransaction[];
  }>;
}

const RELEASABLE_STATUS = "pending";

const AwinUserActivity = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [userDetails, setUserDetails] = useState<AwinUserDetails | null>(null);
  const [transactions, setTransactions] = useState<AwinTransaction[]>([]);
  const [headers, setHeaders] = useState<{ [key: string]: string }>({});
  const [hasDetails, setHasDetails] = useState(false);

  const [stats, setStats] = useState<AwinStats | null>(null);
  const [topBrands, setTopBrands] = useState<AwinTopBrand[]>([]);
  const [recent, setRecent] = useState<AwinRecentPurchase[]>([]);
  const [pagination, setPagination] = useState<OverviewPagination | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [editRow, setEditRow] = useState<AwinTransaction | null>(null);
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const classes = useStyles();
  const notify = useNotify();

  const toInputValue = (value: string | null) => {
    const date = value ? new Date(value) : new Date();
    if (isNaN(date.getTime())) return "";
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  };

  const getHeaderLabel = (key: string, defaultLabel: string) => {
    if (key === "transaction_status") return `${defaultLabel} (Awin)`;
    if (key === "reward_status") return `${defaultLabel} (Sprive)`;
    return defaultLabel;
  };

  const load = useCallback(
    async (searchEmail?: string, nextPage = page, nextSize = pageSize) => {
      setLoading(true);
      try {
        const params: any = { page: nextPage, limit: nextSize };
        if (searchEmail) params.email = searchEmail;

        const response = await axios.get(ENDPOINTS.USER_AWIN_ACTIVITY, getConfigGet(params));
        const firstData = (response?.data as AwinApiResponse)?.data?.[0];
        if (!firstData) {
          notify("No Awin data returned", "warning");
          return;
        }

        setStats(firstData.stats ?? null);
        setTopBrands(firstData.top_brands ?? []);
        setRecent(firstData.recent_purchases ?? []);
        setPagination(firstData.pagination ?? null);

        if (searchEmail) {
          if (firstData.userDetails?.length) {
            setUserDetails(firstData.userDetails[0]);
            setHasDetails(true);

            if (firstData.transactionDetails?.length) {
              const headerRow = firstData.transactionDetails[0];
              const headerMapping: { [key: string]: string } = {};
              Object.keys(headerRow).forEach((key) => {
                headerMapping[key.toLowerCase()] = headerRow[key];
              });
              setHeaders(headerMapping);
              setTransactions(firstData.transactionDetails.filter((_: any, i: number) => i > 0));
            } else {
              setHeaders({});
              setTransactions([]);
            }
            notify("Awin details found successfully", "info");
          } else {
            setHasDetails(false);
            notify("No Awin activity found for this email", "warning");
          }
        }
      } catch (error: any) {
        console.error("Error fetching Awin details:", error);
        notify(error?.response?.data?.message || "Failed to fetch Awin details", "error");
        setHasDetails(false);
      } finally {
        setLoading(false);
      }
    },
    [notify, page, pageSize]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailSubmit = () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }
    setUserDetails(null);
    setTransactions([]);
    setHeaders({});
    setHasDetails(false);
    load(email.trim(), 1, pageSize);
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    load(hasDetails ? email.trim() : undefined, nextPage, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
    load(hasDetails ? email.trim() : undefined, 1, size);
  };

  const handleAddToAwin = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setAddLoading(true);
    try {
      const config = getConfigPost();
      await axios.post(
        ENDPOINTS.USER_AWIN_ACTIVITY,
        {},
        { headers: config, params: { email: email.trim() } }
      );
      notify("Sprive Plus enabled - this unlocks Awin, Partnerize and VDX brands", "info");
      load(email.trim(), page, pageSize);
    } catch (error: any) {
      console.error("Error enabling Sprive Plus:", error);
      notify(error?.response?.data?.message || "Failed to enable Sprive Plus", "error");
    } finally {
      setAddLoading(false);
    }
  };

  const openEditDialog = (transaction: AwinTransaction) => {
    setEditRow(transaction);
    setEditDate(toInputValue(transaction.transaction_date));
  };

  const closeEditDialog = () => {
    setEditRow(null);
    setEditDate("");
  };

  const handleReleaseDateSave = async () => {
    if (!editRow || !editDate) {
      notify("Please pick a transaction date", "error");
      return;
    }

    setSaving(true);
    try {
      const config = getConfigPost();
      await axios.put(
        ENDPOINTS.USER_AWIN_ACTIVITY,
        {
          reward_id: editRow.reward_id,
          transaction_date: `${editDate.replace("T", " ")}:00`,
        },
        { headers: config, params: { email: email.trim() } }
      );

      notify("Transaction date updated. The next transfer run will pay it.", "info");
      closeEditDialog();
      load(email.trim(), page, pageSize);
    } catch (error: any) {
      console.error("Error updating transaction date:", error);
      notify(error?.response?.data?.message || "Failed to update transaction date", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderStats = () => {
    if (!stats) return null;

    const tiles: StatTile[] = [
      { value: stats.purchases.total, label: "Purchases" },
      { value: stats.purchases.pending, label: "Pending" },
      { value: stats.purchases.approved, label: "Approved" },
      { value: stats.purchases.declined, label: "Declined" },
      {
        value: `${stats.purchases.approval_rate_pct}%`,
        label: "Approval rate",
        sub: "observed, not Awin's figure",
      },
      {
        value: stats.purchases.unattributed,
        label: "Unattributed",
        sub: "no user matched — clickRef missing",
        warn: stats.purchases.unattributed > 0,
      },
    ];

    const valueTiles: StatTile[] = [
      { value: money(stats.value.total_sale_amount), label: "Total sales" },
      { value: money(stats.value.approved_sale_amount), label: "Approved sales" },
      { value: money(stats.value.total_commission), label: "Commission (all)" },
      { value: money(stats.value.approved_commission), label: "Commission (approved)" },
      { value: money(stats.rewards.user_amount), label: "Owed to users" },
      { value: money(stats.rewards.paid_to_users), label: "Paid to users" },
      { value: money(stats.rewards.haircut_amount), label: "Sprive margin" },
      { value: stats.reach.distinct_users, label: "Users with purchases" },
    ];

    return <StatPanel title="Awin Performance" tiles={tiles} valueTiles={valueTiles} />;
  };

  const TOP_BRAND_COLUMNS: OverviewColumn[] = [
    { key: "brand_name", label: "Brand" },
    { key: "advertiser_id", label: "Advertiser ID" },
    { key: "purchases", label: "Purchases" },
    { key: "approved_count", label: "Approved" },
    { key: "sale_amount", label: "Sales", money: true },
    { key: "commission", label: "Commission", money: true },
  ];

  const RECENT_COLUMNS: OverviewColumn[] = [
    { key: "transaction_id", label: "Transaction ID" },
    { key: "email", label: "User" },
    { key: "brand_name", label: "Brand" },
    { key: "sale_amount", label: "Sale", money: true },
    { key: "commission_amount", label: "Commission", money: true },
    { key: "transaction_status", label: "Status (Awin)" },
    { key: "transaction_date", label: "Transaction Date", date: true },
    { key: "validation_date", label: "Validated", date: true },
    { key: "rewards_amount", label: "Reward", money: true },
    { key: "reward_status", label: "Reward Status (Sprive)" },
    { key: "source", label: "Source" },
  ];

  const renderUserDetails = () => {
    if (!hasDetails || !userDetails) return null;

    return (
      <Paper className={classes.tableContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Awin User Details
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>User ID</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Brand Clicks
              </TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Sprive Plus Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell}>{userDetails.user_id}</TableCell>
              <TableCell className={classes.tableCell}>
                {userDetails.total_clicks || "Not clicked an Awin brand yet"}
              </TableCell>
              <TableCell className={classes.tableCell}>{userDetails.sprive_plus_status}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderTransactions = () => {
    if (!hasDetails) return null;

    if (!transactions.length) {
      return (
        <Paper className={classes.transactionContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Awin Purchases For This User
          </Typography>
          <Typography className={classes.noDataMessage}>No purchases found</Typography>
        </Paper>
      );
    }

    const keys = Object.keys(headers);

    return (
      <Paper className={classes.transactionContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Awin Purchases For This User
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {keys.map((key) => (
                <TableCell key={key} className={`${classes.tableCell} ${classes.tableHeader}`}>
                  {getHeaderLabel(key, headers[key])}
                </TableCell>
              ))}
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction, index) => (
              <TableRow key={index}>
                {keys.map((key) => (
                  <TableCell key={key} className={classes.tableCell}>
                    {(key === "sale_amount" ||
                      key === "rewards_amount" ||
                      key === "gross_amount") && transaction[key]
                      ? money(transaction[key])
                      : key.includes("date") && transaction[key]
                        ? formatUTCDateTime(transaction[key])
                        : transaction[key]}
                  </TableCell>
                ))}
                <TableCell className={classes.tableCell}>
                  <Tooltip
                    title={
                      !transaction.reward_id
                        ? "No reward row for this purchase yet"
                        : transaction.reward_status !== RELEASABLE_STATUS
                          ? `Already ${transaction.reward_status} - the date can only be changed while pending`
                          : "Change when this reward is released"
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={
                          !transaction.reward_id ||
                          transaction.reward_status !== RELEASABLE_STATUS
                        }
                        onClick={() => openEditDialog(transaction)}
                      >
                        Edit Transaction Date
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <div className={classes.main}>
      <Typography variant="h3" component="h1" className={classes.mainHeading}>
        Awin User Activity
      </Typography>

      <Paper className={classes.card}>
        <div className={classes.form}>
          <TextField
            className={classes.input}
            label="Enter Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleEmailSubmit();
            }}
          />

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEmailSubmit}
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Searching..." : "Search Awin Activity"}
            </Button>

            <Tooltip
              title={
                userDetails?.sprive_plus_enabled
                  ? "Sprive Plus is already enabled for this user"
                  : "Enables Sprive Plus, which unlocks Awin, Partnerize and VDX brands"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAddToAwin}
                  disabled={addLoading || !email.trim() || !!userDetails?.sprive_plus_enabled}
                  startIcon={addLoading ? <CircularProgress size={20} /> : null}
                >
                  {addLoading ? "Adding..." : "Add to Awin"}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        {!hasDetails && email && !loading && (
          <Typography className={classes.noDataMessage}>
            No Awin activity found for this email
          </Typography>
        )}
      </Paper>

      {renderStats()}
      {renderUserDetails()}
      {renderTransactions()}
      <OverviewTable
        title="Top Brands by Commission"
        columns={TOP_BRAND_COLUMNS}
        rows={topBrands}
        emptyMessage="No brand data yet"
        rowKey={(row) => row.advertiser_id}
      />
      <OverviewTable
        title="Latest Purchases"
        columns={RECENT_COLUMNS}
        rows={recent}
        emptyMessage="No purchases yet"
        rowKey={(row, i) => row.transaction_row_id ?? i}
        pagination={pagination}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <Dialog open={!!editRow} onClose={closeEditDialog}>
        <DialogTitle>Edit Transaction Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {editRow?.brand_name} &mdash; {money(editRow?.rewards_amount)}
          </Typography>
          <TextField
            className={classes.input}
            label="Transaction Date (UTC)"
            type="datetime-local"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" className={classes.helperText} component="p">
            Setting a date in the past releases the reward on the next affiliate rewards transfer
            run. The money is not moved by saving this.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReleaseDateSave}
            color="primary"
            variant="contained"
            disabled={saving || !editDate}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default AwinUserActivity;
